/**
 * Shared primitives for sectioned filter panels.
 *
 * Every reworked filter panel (dashboard, purchasing tabs, quality) groups its
 * filters under headings. Those panels previously each carried their own
 * section shell, which drifted apart visually — the acceptance criteria for the
 * rework require one consistent layout across every view, so the shell lives
 * here and each panel supplies only its fields.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * A labelled group of related filters.
 *
 * @param title - section heading, rendered upper-case
 * @param icon - optional leading glyph; panels without one still align
 * @param bare - render the heading without its shaded bar, for panels already
 *   sitting inside a card that provides its own padding
 */
export function FilterSection({
  title,
  icon: Icon,
  bare = false,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  bare?: boolean;
  children: ReactNode;
}) {
  if (bare) {
    return (
      <div className="space-y-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {title}
        </h4>
        {children}
      </div>
    );
  }

  return (
    <section className="border-t first:border-t-0">
      <div className="flex items-center gap-2 px-6 py-2 bg-muted/40">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      </div>
      <div className="px-6 py-4">{children}</div>
    </section>
  );
}

/**
 * Horizontal rule between filter sections.
 *
 * Inset from the card edges so it reads as a divider *inside* the panel rather
 * than a border on the card itself. A panel using this should also neutralise
 * the section shell's own full-bleed rule — `[&>section]:border-t-0` on the
 * wrapper — or the two draw on top of each other.
 *
 * This is the rule the Purchasing panels already share (PO / SO Headers,
 * Deliveries, ASN, Forecast, PO Lines); it lives here so new panels pick it up
 * from the same place they get `FilterSection`.
 */
export const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

/** Standard 4-up responsive grid used inside every filter section. */
export function FilterGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
  );
}

/**
 * Label + control pairing so every field in every panel is styled identically.
 *
 * `label` accepts a node, not just a string, so a panel can append an inline
 * hint (e.g. a muted "(none configured)") without hand-rolling its own label
 * and drifting from this styling.
 *
 * `htmlFor` ties the label to a control that carries an id, so the pairing
 * is also an accessible one (screen readers, `getByLabelText`) rather than
 * purely visual. Optional because most panels wrap controls without ids.
 */
export function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-medium mb-1.5 block text-foreground">{label}</label>
      {children}
    </div>
  );
}

/**
 * Shared control styling. Kept as a constant rather than a wrapper component so
 * native <select> / <input> elements stay exactly what they were — the filter
 * rework explicitly forbids swapping input types while restyling.
 */
export const filterControlClass =
  'w-full px-3 py-2.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background';

// CR-6446/CR-6448: Re-export CollapsibleSection for Supplier View accordion-style filters
export { CollapsibleSection } from './CollapsibleSection';
